// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { useI18n } from '../i18n';

const inp = {
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: '1px solid var(--gray)', fontSize: 13,
  boxSizing: 'border-box', outline: 'none',
  background: 'var(--bg-input)', color: 'var(--black)',
};

// Sous-flow QR → code → recovery codes. Le mode d'auth (session normale
// opt-in depuis ProfileModal, ou temp_token setup forcé depuis Login.js)
// est injecté par l'appelant via les props onSetup/onConfirm — ce composant
// ne connaît que la forme { secret, qr_code } / { recovery_codes, ... }.
const MfaSetupFlow = ({ onSetup, onConfirm, onComplete, onClose }) => {
  const { t } = useI18n();
  const [step, setStep] = useState('loading'); // loading | enter_code | recovery_codes
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmResult, setConfirmResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await onSetup();
        if (cancelled) return;
        setQrCode(data.qr_code);
        setSecret(data.secret);
        setStep('enter_code');
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    if (!code.trim()) { setError(t('mfa_error_invalid_code')); return; }
    setLoading(true);
    try {
      const result = await onConfirm(code.trim());
      setConfirmResult(result);
      setRecoveryCodes(result.recovery_codes || []);
      setStep('recovery_codes');
    } catch (err) {
      setError(err.message || t('mfa_error_invalid_code'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--gray)', padding: '24px 28px', maxWidth: 400, width: '100%', boxShadow: 'var(--shadow-md)' }}>

        {step === 'loading' && (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>
            {error ? <span style={{ color: '#ef4444' }}>❌ {error}</span> : t('mfa_loading_setup')}
          </div>
        )}

        {step === 'enter_code' && (
          <form onSubmit={handleConfirm}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--black)', marginBottom: 10 }}>
              🔐 {t('mfa_section_title')}
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.6 }}>
              {t('mfa_setup_intro')}
            </p>
            {qrCode && (
              <div style={{ textAlign: 'center', marginBottom: 14 }}>
                <img src={qrCode} alt="QR code MFA" style={{ width: 180, height: 180 }} />
              </div>
            )}
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{t('mfa_setup_manual_hint')}</p>
            <div style={{ fontSize: 12, fontFamily: 'monospace', background: 'var(--bg-input)', borderRadius: 6, padding: '6px 10px', marginBottom: 14, wordBreak: 'break-all' }}>
              {secret}
            </div>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('mfa_code_label')}</label>
            <input
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder={t('mfa_code_placeholder')}
              style={{ ...inp, textAlign: 'center', fontSize: 18, letterSpacing: 4 }}
              maxLength={8}
              autoFocus
            />
            {error && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>❌ {error}</p>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, background: 'transparent', border: '1px solid var(--gray)', fontSize: 13, cursor: 'pointer', color: 'var(--black)' }}>
                {t('cancel')}
              </button>
              <Button type="submit" disabled={loading}>
                {loading ? t('mfa_verifying') : t('mfa_confirm_cta')}
              </Button>
            </div>
          </form>
        )}

        {step === 'recovery_codes' && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--black)', marginBottom: 10 }}>
              🗝️ {t('mfa_recovery_codes_title')}
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.6 }}>
              {t('mfa_recovery_codes_intro')}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 18 }}>
              {recoveryCodes.map((rc) => (
                <div key={rc} style={{ fontSize: 13, fontFamily: 'monospace', background: 'var(--bg-input)', borderRadius: 6, padding: '6px 10px', textAlign: 'center' }}>
                  {rc}
                </div>
              ))}
            </div>
            <Button onClick={() => onComplete(confirmResult)} style={{ width: '100%' }}>
              {t('mfa_recovery_codes_done_cta')}
            </Button>
          </div>
        )}

      </div>
    </div>
  );
};

export default MfaSetupFlow;
